import {
  importGoogleMapsLibrary,
  loadGoogleMapsPlacesLibrary,
} from 'lib/utils/googleMaps/loadGoogleMapsMapLibrary';
import {
  parseLocalityFromResult,
  withResolvedZipcode,
  type GeocodedLocation,
} from './geocodeLocation';

export type LocationSuggestion = {
  description: string;
  placeId: string;
};

const MIN_INPUT_LENGTH = 2;

const ensureGoogleMapsPlacesLoaded = async (mapApiKey?: string): Promise<void> => {
  await loadGoogleMapsPlacesLibrary(mapApiKey);
};

/**
 * Fetches US city / state / ZIP suggestions via Google Places Autocomplete (not Coveo).
 */
export const fetchLocationSuggestions = async (
  input: string,
  mapApiKey?: string
): Promise<LocationSuggestion[]> => {
  const trimmed = input?.trim() ?? '';
  if (trimmed.length < MIN_INPUT_LENGTH) {
    return [];
  }

  await ensureGoogleMapsPlacesLoaded(mapApiKey);

  if (typeof window === 'undefined' || !window.google?.maps?.places?.AutocompleteService) {
    return [];
  }

  const autocompleteService = new window.google.maps.places.AutocompleteService();

  return new Promise((resolve) => {
    autocompleteService.getPlacePredictions(
      {
        input: trimmed,
        componentRestrictions: { country: 'us' },
        types: ['(regions)'],
      },
      (predictions, status) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !predictions?.length) {
          resolve([]);
          return;
        }

        resolve(
          predictions.map((prediction) => ({
            description: prediction.description,
            placeId: prediction.place_id,
          }))
        );
      }
    );
  });
};

/**
 * Resolves a Places placeId to latitude/longitude via Geocoder.
 * When the place result has no postal_code, reverse-geocodes once to resolve a ZIP.
 */
export const resolvePlaceCoords = async (
  placeId: string,
  mapApiKey?: string
): Promise<GeocodedLocation | null> => {
  const trimmed = placeId?.trim();
  if (!trimmed) {
    return null;
  }

  await ensureGoogleMapsPlacesLoaded(mapApiKey);
  await importGoogleMapsLibrary('geocoding', mapApiKey);

  if (typeof window === 'undefined' || !window.google?.maps?.Geocoder) {
    return null;
  }

  const geocoded = await new Promise<GeocodedLocation | null>((resolve) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ placeId: trimmed }, (results, status) => {
      if (status === 'OK' && results && results.length > 0) {
        const location = results[0]?.geometry?.location;
        const latitude = location?.lat();
        const longitude = location?.lng();

        if (typeof latitude === 'number' && typeof longitude === 'number') {
          resolve({ latitude, longitude, ...parseLocalityFromResult(results[0]) });
          return;
        }
      }
      resolve(null);
    });
  });

  if (!geocoded) {
    return null;
  }

  return withResolvedZipcode(geocoded, mapApiKey);
};
